import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { Filter } from "lucide-react";
import { listPublishedSimulations } from "@/lib/api/lxp-simulations.functions";

const listQO = queryOptions({
  queryKey: ["lxp", "simulations", "all"],
  queryFn: () => listPublishedSimulations({ data: { limit: 100 } }),
});

export const Route = createFileRoute("/lexpractiq/simulations/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(listQO),
  head: () => ({
    meta: [
      { title: "Simulation Marketplace | SA LexPractiq" },
      { name: "description", content: "Browse practical legal simulations across labour, commercial, compliance and constitutional law." },
      { property: "og:title", content: "Simulation Marketplace — SA LexPractiq" },
      { property: "og:description", content: "Real SA legal scenarios. AI supervising attorney. Verified certificates." },
    ],
  }),
  component: MarketplacePage,
});

function MarketplacePage() {
  const { data } = useSuspenseQuery(listQO);
  const [area, setArea] = useState<string>("all");
  const [difficulty, setDifficulty] = useState<string>("all");
  const [duration, setDuration] = useState<string>("all");
  const [skill, setSkill] = useState<string>("all");
  const [query, setQuery] = useState<string>("");
  const areas = useMemo(() => Array.from(new Set(data.items.map((i) => i.practice_area))), [data]);
  const skills = useMemo(() => {
    const s = new Set<string>();
    data.items.forEach((i) => (i.competencies || []).forEach((c: any) => s.add(c.label)));
    return Array.from(s).sort();
  }, [data]);
  const filtered = data.items.filter((i) => {
    if (area !== "all" && i.practice_area !== area) return false;
    if (difficulty !== "all" && i.difficulty !== difficulty) return false;
    if (duration !== "all") {
      const h = i.duration_min / 60;
      if (duration === "short" && h > 2) return false;
      if (duration === "medium" && (h <= 2 || h > 5)) return false;
      if (duration === "long" && h <= 5) return false;
    }
    if (skill !== "all" && !(i.competencies || []).some((c: any) => c.label === skill)) return false;
    if (query.trim() && !`${i.title} ${i.summary} ${i.practice_area}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  return (
    <div className="lxp-container" style={{ padding: "3rem 1.25rem" }}>
      <div style={{ marginBottom: "1.5rem" }}>
        <span className="lxp-badge">Legal Experience Platform · Simulation Marketplace</span>
        <h1 style={{ fontSize: "2rem", margin: "0.5rem 0 0.5rem", letterSpacing: "-0.02em" }}>Pick your next legal experience</h1>
        <p className="lxp-muted" style={{ margin: 0 }}>Real South African legal scenarios. AI supervising attorney. Verified portfolio.</p>
      </div>

      <div className="lxp-card" style={{ marginBottom: "1.5rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "0.6rem" }}>
          <label style={{ fontSize: "0.75rem" }}>
            <div className="lxp-muted" style={{ marginBottom: 4 }}><Filter size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />Search</div>
            <input className="lxp-input" placeholder="Keyword…" value={query} onChange={(e) => setQuery(e.target.value)} />
          </label>
          <label style={{ fontSize: "0.75rem" }}>
            <div className="lxp-muted" style={{ marginBottom: 4 }}>Practice area</div>
            <select className="lxp-select" value={area} onChange={(e) => setArea(e.target.value)}>
              <option value="all">All areas</option>
              {areas.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <label style={{ fontSize: "0.75rem" }}>
            <div className="lxp-muted" style={{ marginBottom: 4 }}>Difficulty</div>
            <select className="lxp-select" value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
              <option value="all">All levels</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </label>
          <label style={{ fontSize: "0.75rem" }}>
            <div className="lxp-muted" style={{ marginBottom: 4 }}>Duration</div>
            <select className="lxp-select" value={duration} onChange={(e) => setDuration(e.target.value)}>
              <option value="all">Any</option>
              <option value="short">Under 2h</option>
              <option value="medium">2–5h</option>
              <option value="long">5h+</option>
            </select>
          </label>
          <label style={{ fontSize: "0.75rem" }}>
            <div className="lxp-muted" style={{ marginBottom: 4 }}>Skill built</div>
            <select className="lxp-select" value={skill} onChange={(e) => setSkill(e.target.value)}>
              <option value="all">Any skill</option>
              {skills.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>
        <div className="lxp-muted" style={{ fontSize: "0.8rem", marginTop: "0.75rem" }}>
          {filtered.length} simulation{filtered.length === 1 ? "" : "s"} match your filters
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "1rem" }}>
        {filtered.map((s) => (
          <Link
            key={s.slug}
            to="/lexpractiq/simulations/$slug"
            params={{ slug: s.slug }}
            className="lxp-card"
            style={{ textDecoration: "none", display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span className="lxp-badge">{s.practice_area}</span>
              <span style={{ fontSize: "0.7rem", color: "var(--lxp-text-muted)", textTransform: "uppercase" }}>{s.difficulty}</span>
            </div>
            <h3 style={{ margin: 0, fontSize: "1.05rem", lineHeight: 1.3 }}>{s.title}</h3>
            <p className="lxp-muted" style={{ fontSize: "0.85rem", margin: 0, lineHeight: 1.55, flex: 1 }}>{s.summary}</p>
            <div style={{ display: "flex", gap: "0.75rem", fontSize: "0.75rem", color: "var(--lxp-text-muted)", borderTop: "1px solid var(--lxp-border)", paddingTop: "0.75rem" }}>
              <span>⏱ {Math.round(s.duration_min / 60)}h</span>
              <span>⚡ {s.xp_reward} XP</span>
              <span style={{ marginLeft: "auto", color: "var(--lxp-accent)" }}>Start →</span>
            </div>
          </Link>
        ))}
      </div>
      {filtered.length === 0 && (
        <div className="lxp-card" style={{ textAlign: "center", padding: "3rem" }}>
          <p className="lxp-muted">No simulations match your filters yet. Try clearing them or check back soon.</p>
        </div>
      )}
    </div>
  );
}
